import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

const Refund = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="refund-page">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2" data-testid="refund-title">Refund Policy</h1>
        <p className="text-sm text-gray-500 mb-8">Last Updated: February 1, 2016</p>

        {/* Prominent No-Refund Notice */}
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-10" data-testid="no-refund-notice">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center shrink-0">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-red-800 mb-2">ALL SALES ARE FINAL — NO REFUNDS</h2>
              <p className="text-red-700 font-medium">Due to the digital nature of our products, PaperworkDepot does not offer refunds, returns, exchanges, or credits under any circumstances once a purchase has been completed and a download link has been made available. By completing a purchase on our Website, you acknowledge and agree to this policy.</p>
            </div>
          </div>
        </div>

        <div className="prose prose-gray max-w-none space-y-8 text-gray-700 leading-relaxed">

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">1. Digital Products — No Refunds</h2>
            <p><strong>1.1</strong> All products sold on PaperworkDepot.com are digital goods delivered electronically in PDF format. Once a purchase is completed and the download link is made available to you — whether or not you actually download the file — the transaction is final and non-refundable.</p>
            <p className="mt-2"><strong>1.2</strong> This policy applies to all products without exception, including but not limited to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Individual guides ($9.99)</li>
              <li>5-Guide Bundles ($39)</li>
              <li>10-Guide Bundles ($79)</li>
              <li>Category Bundles ($149)</li>
              <li>Lawsuit Starter Pack ($150)</li>
              <li>All Access Vault ($700)</li>
              <li>Any promotional or discounted products</li>
            </ul>
            <p className="mt-2"><strong>1.3</strong> This no-refund policy is in effect regardless of whether you have downloaded, opened, read, or used the purchased product. The availability of the download link constitutes delivery of the digital product.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">2. Why We Do Not Offer Refunds</h2>
            <p>Digital products cannot be "returned" in the same way that physical goods can. Once a digital file has been made available for download:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>It is impossible to verify that the purchaser has not retained, copied, or distributed the file</li>
              <li>The intellectual property contained in the product has been fully delivered</li>
              <li>The cost of creating, maintaining, and updating the product has been incurred</li>
            </ul>
            <p className="mt-2">For these reasons, refunds on digital products would expose us to significant risk of abuse and loss of intellectual property.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">3. Pre-Purchase Protections</h2>
            <p>To help you make an informed purchasing decision, we provide the following before purchase:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><strong>Free Preview:</strong> Every guide includes a 2-page preview so you can review the content and format before buying</li>
              <li><strong>Detailed Descriptions:</strong> Each product listing includes a comprehensive description of what is covered in the guide</li>
              <li><strong>Free Guide:</strong> We offer a <a href="/free-guide" className="text-red-700 hover:text-red-800 underline">free downloadable guide</a> so you can experience the quality and format of our products at no cost before making a purchase</li>
              <li><strong>Customer Support:</strong> Our <a href="/support" className="text-red-700 hover:text-red-800 underline">support team</a> is available to answer any questions about our products before you buy</li>
            </ul>
            <p className="mt-2">We strongly encourage you to review the preview, read the description, and contact us with any questions BEFORE making a purchase.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">4. Chargebacks and Disputes</h2>
            <p><strong>4.1</strong> If you initiate a chargeback, payment dispute, or reversal with your bank, credit card company, or payment processor after purchasing a product from PaperworkDepot, we reserve the right to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Immediately suspend and terminate your access to all purchased products and download links</li>
              <li>Report the fraudulent chargeback to relevant fraud databases</li>
              <li>Pursue recovery of the full purchase amount plus all associated chargeback fees, administrative costs, and collection expenses</li>
              <li>Engage a collections agency to recover any outstanding amounts</li>
              <li>Pursue legal action in the jurisdiction specified in our Terms of Use</li>
            </ul>
            <p className="mt-2"><strong>4.2</strong> Initiating a chargeback on a legitimate purchase of a digital product that was properly delivered constitutes fraud. By completing your purchase, you acknowledge that you understand this policy and agree not to initiate chargebacks for any reason.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">5. Technical Issues</h2>
            <p><strong>5.1</strong> If you experience a technical issue with downloading your purchased product (e.g., broken download link, corrupted file), please contact us at <a href="mailto:support@paperworkdepot.com" className="text-red-700 hover:text-red-800 underline">support@paperworkdepot.com</a> within 48 hours of your purchase. We will work to resolve the technical issue and ensure you receive access to your purchased product.</p>
            <p className="mt-2"><strong>5.2</strong> Technical support for download issues is NOT a refund. We will re-issue the download link or provide an alternative delivery method, but we will not issue a monetary refund.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">6. Duplicate Purchases</h2>
            <p>If you accidentally purchase the same product twice, please contact us at <a href="mailto:support@paperworkdepot.com" className="text-red-700 hover:text-red-800 underline">support@paperworkdepot.com</a> within 24 hours of the duplicate purchase. We may, at our sole discretion, issue a store credit (not a refund) for the duplicate purchase amount, which can be applied toward a future purchase. This is not guaranteed and will be evaluated on a case-by-case basis.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">7. Satisfaction Disclaimer</h2>
            <p>While we strive to provide high-quality, accurate, and comprehensive legal documentation guides, we cannot guarantee that every product will meet your individual expectations or requirements. Our products are educational templates and guides, not customized legal documents. Dissatisfaction with the content, format, applicability, or outcome of using a product is not grounds for a refund.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">8. Promotional and Bundle Purchases</h2>
            <p><strong>8.1</strong> Products purchased as part of a bundle or promotional offer are subject to the same no-refund policy. Individual guides within a bundle cannot be returned or exchanged separately.</p>
            <p className="mt-2"><strong>8.2</strong> If a product is subsequently offered at a lower price or as part of a promotion after your purchase, you are not entitled to a price adjustment, credit, or partial refund.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">9. Agreement</h2>
            <p>By completing a purchase on PaperworkDepot.com, you confirm that you have read, understood, and agree to this Refund Policy. You acknowledge that:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>You are purchasing a digital product that is delivered immediately upon payment</li>
              <li>All sales are final and non-refundable</li>
              <li>You waive any right to a refund, chargeback, or payment dispute</li>
              <li>You have had the opportunity to review the product preview and description before purchasing</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">10. Contact Us</h2>
            <p>For technical issues with downloads or questions about this policy:</p>
            <p className="mt-2">
              <strong>PaperworkDepot</strong><br />
              Email: <a href="mailto:support@paperworkdepot.com" className="text-red-700 hover:text-red-800 underline">support@paperworkdepot.com</a><br />
              Phone: 1-800-555-1234
            </p>
          </section>

        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Refund;
